import { ContentWrapper } from 'ui/atoms/ContentWrapper'
import {
  EQUIPMENT_DETAILS,
  EQUIPMENT_DETAILS_FORM_VALUES,
} from 'texts/equipmentDetails'
import {
  Multiselect,
  Select,
  Form,
  FormItem,
  Checkbox,
  Tooltip,
  FormAlertLabel,
} from '@frontend/design_system'
import { useMemo, useState } from 'react'
import { RADIO_BUTTONS } from './constants'
import { RadioButton } from 'ui/atoms/RadioButton'
import { useSelectValues } from 'hooks/useSelectValues'
import { EquipmentDetailsFormProps } from './types'
import { PriceForm } from './components/PriceForm'
import { useValidationRules } from './hooks/useValidationRules'
import styles from './styles.module.scss'
import { EQUIPMENT_FORM_ID } from '../../constants'
import { FormChildren, FormItemChildren } from 'types/form'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'
import { InfoIcon } from 'ui/icons'
import {
  isMigratedUserFlowSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { CreationSource } from 'redux/login/types'
import { MigratedUserBadge } from 'ui/components/MigratedUserBadge'

export const EquipmentDetailsForm = ({
  initialValues,
  handleSubmit,
  equipmentDetailsData,
  isEditing,
}: EquipmentDetailsFormProps) => {
  const validationRules = useValidationRules()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const userData = useAppSelector(userDataSelector)
  const isMigratedUserFlow = useAppSelector(isMigratedUserFlowSelector)

  const onSubmit = (data: Record<string, unknown>, validate: boolean) => {
    setIsSubmitting(false)
    if (validate) {
      setIsSubmitting(true)
      handleSubmit(data)
    }
  }

  const extrasUniqueValues = useMemo(
    () =>
      Array.from(
        new Map(
          equipmentDetailsData.extras
            .slice()
            .sort((a, b) => {
              if (a.id === initialValues.extraId) return 1
              if (b.id === initialValues.extraId) return -1
              return 0
            })
            .map((item) => [item.name, item])
        ).values()
      ),
    [initialValues.extraId]
  )

  const locationsSelectValues = useSelectValues(equipmentDetailsData.locations)
  const extrasSelectValues = useSelectValues(extrasUniqueValues)

  return (
    <Form
      initValues={initialValues}
      onSubmit={onSubmit}
      className={styles['form-wrapper']}
      formId={EQUIPMENT_FORM_ID}
      reduxId={EQUIPMENT_FORM_ID}
      isMountValidate={isEditing && isMigratedUserFlow}
    >
      {({ values, isDirty, errors }: FormChildren) => (
        <>
          <MigratedUserBadge errors={errors} />
          <ContentWrapper
            className={styles.form}
            title={EQUIPMENT_DETAILS.FORM_TITLE}
          >
            <>
              <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
              <div className={styles['form-items-row']}>
                <FormItem
                  id={EQUIPMENT_DETAILS_FORM_VALUES.EQUIPMENT_NAME.ID}
                  className={styles['form-item']}
                  rules={
                    validationRules[
                      EQUIPMENT_DETAILS_FORM_VALUES.EQUIPMENT_NAME.ID
                    ]
                  }
                >
                  {({ value, error, onChange }: FormItemChildren) => (
                    <Select
                      size="large"
                      error={error}
                      items={extrasSelectValues}
                      placeholder={
                        EQUIPMENT_DETAILS_FORM_VALUES.EQUIPMENT_NAME.PLACEHOLDER
                      }
                      onChange={onChange}
                      isRequired
                      label={EQUIPMENT_DETAILS_FORM_VALUES.EQUIPMENT_NAME.LABEL}
                      selectedValue={value}
                    />
                  )}
                </FormItem>
                <FormItem
                  id={EQUIPMENT_DETAILS_FORM_VALUES.LOCATION.ID}
                  className={styles['form-item']}
                  rules={
                    validationRules[EQUIPMENT_DETAILS_FORM_VALUES.LOCATION.ID]
                  }
                >
                  {({ value, error, onChange }: FormItemChildren) => (
                    <Multiselect
                      error={error}
                      onChange={onChange}
                      value={value}
                      label={EQUIPMENT_DETAILS_FORM_VALUES.LOCATION.LABEL}
                      placeholder={
                        EQUIPMENT_DETAILS_FORM_VALUES.LOCATION.PLACEHOLDER
                      }
                      unitName={{
                        plural: EQUIPMENT_DETAILS.SEARCH_VALUE,
                      }}
                      items={locationsSelectValues}
                      withAllSelect
                      isRequired
                    />
                  )}
                </FormItem>
              </div>
              <PriceForm formValues={values} />
              <FormItem
                id={EQUIPMENT_DETAILS_FORM_VALUES.SUM_FOR.ID}
                className={styles['radio-buttons']}
                rules={
                  validationRules[EQUIPMENT_DETAILS_FORM_VALUES.SUM_FOR.ID]
                }
              >
                {({
                  value,
                  onChange,
                  onChangeField,
                  error,
                }: FormItemChildren) => (
                  <>
                    <div className={styles['radio-buttons-wrapper']}>
                      {RADIO_BUTTONS.map(({ label, id }) => (
                        <RadioButton
                          labelTypographyName="Button2"
                          key={id}
                          id={id}
                          label={label}
                          onChange={(value) => {
                            onChange(value)
                            onChangeField(
                              '',
                              EQUIPMENT_DETAILS_FORM_VALUES.MAX_AMOUNT.ID
                            )
                          }}
                          checked={id === value}
                        />
                      ))}
                    </div>
                    {error && (
                      <FormAlertLabel
                        label={error}
                        position="static"
                        type="error"
                        typographyName="body2WMedium"
                      />
                    )}
                  </>
                )}
              </FormItem>
              {userData.creationSource === CreationSource.Migrated && (
                <div>
                  <FormItem id={EQUIPMENT_DETAILS_FORM_VALUES.REQUIRED.ID}>
                    {({ value, onChange }: FormItemChildren) => (
                      <Checkbox
                        checked={value}
                        onChange={onChange}
                        variant="primary"
                        typographyName="Button2"
                        label={
                          <div className={styles['required-checkbox-label']}>
                            {EQUIPMENT_DETAILS_FORM_VALUES.REQUIRED.LABEL}{' '}
                            <Tooltip
                              popupContent={
                                EQUIPMENT_DETAILS_FORM_VALUES.REQUIRED.TOOLTIP
                              }
                              size="primary"
                              placement="top"
                              alignSelf="center"
                            >
                              <InfoIcon color="blue700" size="small" />
                            </Tooltip>
                          </div>
                        }
                        borderVariant="grey"
                        size="secondary"
                      />
                    )}
                  </FormItem>
                </div>
              )}
            </>
          </ContentWrapper>
        </>
      )}
    </Form>
  )
}
